// ENT-3797: shared helpers for the vendor team invitation acceptance path.
//
// An invited user who already has an account gets a `vendor_team_members` row
// with `accepted_at = NULL`. Every vendor RLS policy gates on
// `accepted_at IS NOT NULL`, so until the invitee accepts, the row is inert.
// These helpers load a user's own pending invitations and drive the
// `accept_vendor_team_invitation` RPC that flips `accepted_at`.
import { createClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { normalizeDomainInput } from '@/lib/domainNormalization';

// ENT-4086 R17-A: `supabase.rpc()` returns a PostgREST builder that has NO
// `setHeader()` method, so the R16-B bearer binding threw at runtime. To bind an
// RPC to a specific asserted identity we build a request-scoped client whose
// Authorization header carries that bearer (persistSession:false so it never
// touches the shared auth-storage), mirroring the canonical per-request client
// pattern. Env is read the same way as integrations/supabase/client.ts.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '';

function bearerScopedClient(accessToken: string) {
  return createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: { headers: { Authorization: `Bearer ${accessToken}` } },
  });
}

export type VendorInvitationRole = 'owner' | 'admin' | 'member';

export interface PendingVendorInvitation {
  id: string;
  vendorProfileId: string;
  role: VendorInvitationRole;
  invitedAt: string;
  companyName: string | null;
  domain: string | null;
}

interface PendingInvitationRow {
  id: string;
  vendor_profile_id: string;
  role: VendorInvitationRole;
  invited_at: string;
  vendor_profile?: {
    id: string;
    marketplace_listing_id: string;
  } | null;
}

/**
 * Load the caller's own pending (unaccepted) vendor team invitations. RLS allows
 * a user to read their own `vendor_team_members` rows regardless of
 * `accepted_at`, so this returns invitations the rest of the app deliberately
 * hides (the active-membership reads filter `accepted_at IS NOT NULL`).
 * Company name / domain are best-effort — resolved from the marketplace listing
 * where RLS permits — and never block the accept action.
 */
export interface PendingVendorInvitationsResult {
  invitations: PendingVendorInvitation[];
  error: boolean;
}

/**
 * Resilient variant used by the context/banner surfaces where "load failed" and
 * "no invitations" are treated the same (the banner simply does not render).
 */
export async function listPendingVendorInvitations(
  userId: string,
): Promise<PendingVendorInvitation[]> {
  return (await fetchPendingVendorInvitations(userId)).invitations;
}

/**
 * Error-aware variant for the acceptance page, where an RLS/network/transient
 * failure must NOT be mistaken for "no invitation" (which would bounce a genuine
 * invitee to /vendor and the unavailable-profile state). Callers keep the page
 * open and offer a retry when `error` is true.
 */
export async function fetchPendingVendorInvitations(
  userId: string,
): Promise<PendingVendorInvitationsResult> {
  try {
    return { invitations: await loadPendingVendorInvitations(userId), error: false };
  } catch (error) {
    console.error('Error loading pending vendor invitations:', error);
    return { invitations: [], error: true };
  }
}

async function loadPendingVendorInvitations(
  userId: string,
): Promise<PendingVendorInvitation[]> {
  const { data, error } = await supabase
    .from('vendor_team_members')
    .select('id, vendor_profile_id, role, invited_at, vendor_profile:vendor_profiles(id, marketplace_listing_id)')
    .eq('user_id', userId)
    .is('accepted_at', null)
    .order('invited_at', { ascending: false });

  if (error) {
    // Throw so fetchPendingVendorInvitations reports error:true. A normal
    // PostgREST `{ error }` result (RLS/network) must NOT be swallowed to [] —
    // the acceptance page distinguishes "load failed" (retry) from "no
    // invitations" (redirect), and conflating them strands a real invitee.
    throw error;
  }

  const rows = (data ?? []) as unknown as PendingInvitationRow[];
  const listingIds = [
    ...new Set(
      rows
        .map((row) => row.vendor_profile?.marketplace_listing_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];

  const listingMap = new Map<string, { company_name: string | null; domain: string | null }>();
  if (listingIds.length > 0) {
    const { data: listings } = await supabase
      .from('marketplace_listings')
      .select('id, company_name, domain')
      .in('id', listingIds);
    for (const listing of listings ?? []) {
      listingMap.set(listing.id, {
        company_name: listing.company_name ?? null,
        domain: listing.domain ?? null,
      });
    }
  }

  return rows.map((row) => {
    const listing = row.vendor_profile?.marketplace_listing_id
      ? listingMap.get(row.vendor_profile.marketplace_listing_id)
      : undefined;
    return {
      id: row.id,
      vendorProfileId: row.vendor_profile_id,
      role: row.role,
      invitedAt: row.invited_at,
      companyName: listing?.company_name ?? null,
      domain: normalizeDomainInput(listing?.domain ?? null) || null,
    };
  });
}

export function mapAcceptInvitationError(message?: string | null): string {
  const raw = message ?? '';
  if (raw.includes('invitation_not_found')) return 'This invitation is no longer available.';
  if (raw.includes('not_authenticated')) return 'Please sign in to accept this invitation.';
  if (raw.includes('vendor_profile_required')) return 'This invitation is missing its supplier profile.';
  return 'Failed to accept invitation. Please try again.';
}

/**
 * Accept a single pending invitation for the current user. The backend RPC keys
 * authorization on auth.uid() and only ever sets `accepted_at` (never role), so
 * a caller can only accept their own invitation.
 *
 * ENT-4086 R16-B/R17-A: when `accessToken` (the invitee's ASSERTED session token,
 * from `assertVerifiedSession()`) is supplied, the RPC runs on a request-scoped
 * client bound to THAT identity via an Authorization bearer — the same intent as
 * R12-A on the org claim, but for a PostgREST RPC (which, unlike
 * `functions.invoke`, takes no per-call headers option, and whose builder has no
 * `setHeader`). Without it, a session swapped in another tab between the identity
 * assert and this call would run `accept_vendor_team_invitation` as the
 * REPLACEMENT account (`auth.uid()` = whoever is current), not the verified
 * invitee. In-app callers that omit the token keep the global-session client.
 */
export async function acceptVendorTeamInvitation(
  vendorProfileId: string,
  accessToken?: string,
): Promise<{ error: string | null }> {
  const client = accessToken ? bearerScopedClient(accessToken) : supabase;
  const { error } = (await client.rpc(
    'accept_vendor_team_invitation' as never,
    { p_vendor_profile_id: vendorProfileId } as never,
  )) as unknown as { error: { message?: string } | null };

  if (error) {
    console.error('Error accepting vendor invitation:', error);
    return { error: mapAcceptInvitationError(error.message) };
  }
  return { error: null };
}
